import type { NetlinkingBlock, Slug } from "@/modules/jobSearchPrefiltered/types"
import type { FunctionComponent } from "react"

import classnames from "classnames"
import { useState } from "react"

import { Link } from "@jobteaser/ui-kit/components/Link/Link"
import { Text, TEXT_TYPES, TEXT_WEIGHTS } from "@jobteaser/ui-kit/components/Text/Text"

import { useTranslation } from "@/modules/i18n/components/useTranslation"
import { useTrackGenericClickEvent } from "@/modules/tracking/useTrackGenericClickEvent"

import { Tag } from "./Tag"

import styles from "./NetlinkingBlockItem.module.css"

export type NetlinkingBlockProps = {
  className?: string
  currentData: Slug
  data: NetlinkingBlock
}

const MAX_DISPLAYED_NETLINKING_TAGS = 5

const TITLE_TRANSLATION_KEY_PER_TYPE: Record<NetlinkingBlock["type"], `${"job_search_prefiltered.content"}.${string}`> =
  {
    NETLINKING_BLOCK_TYPE_CONTRACT: `job_search_prefiltered.content.netlinkingTitlePerCity`,
    NETLINKING_BLOCK_TYPE_FUNCTION: `job_search_prefiltered.content.netlinkingTitlePerCityAndFunction`,
    NETLINKING_BLOCK_TYPE_INDUSTRY: `job_search_prefiltered.content.netlinkingTitlePerCityAndIndustry`,
    NETLINKING_BLOCK_TYPE_UNSPECIFIED: `job_search_prefiltered.content.netlinkingTitlePerCity`,
  }

export const NetlinkingBlockItem: FunctionComponent<NetlinkingBlockProps> = ({
  className,
  currentData,
  data: { slugs, type },
}) => {
  const { t } = useTranslation(["job_search_prefiltered"])
  const trackEvent = useTrackGenericClickEvent()
  const [isExpanded, setIsExpanded] = useState(slugs.length <= MAX_DISPLAYED_NETLINKING_TAGS)
  const displayedSlugs = isExpanded ? slugs : slugs.slice(0, MAX_DISPLAYED_NETLINKING_TAGS)

  const handleDisplayMore = (event: MouseEvent): void => {
    event.stopPropagation()
    trackEvent("prefiltered-jobsearches-netlinking-block-see-more")
    setIsExpanded(true)
  }

  return (
    <section
      className={classnames(styles.netlinkingBlock, className)}
      data-testid="PrefilteredJobSearch-NetlinkingBlock"
    >
      <Text tag="h2" type={TEXT_TYPES.EDITO} weight={TEXT_WEIGHTS.SEMI_BOLD} className={styles.title} resetSpacing>
        {t(TITLE_TRANSLATION_KEY_PER_TYPE[type], currentData.params)}
      </Text>

      <div className={styles.tags}>
        {displayedSlugs.map(slug => (
          <Tag slug={slug} key={slug.slug} />
        ))}
      </div>

      {!isExpanded && (
        // eslint-disable-next-line jsx-a11y/anchor-is-valid
        <Link
          isButton
          onClick={handleDisplayMore}
          className={styles.seeMore}
          data-testid="PrefilteredJobSearch-NetlinkingBlock__seeMore"
        >
          <Text type={TEXT_TYPES.SMALL} weight={TEXT_WEIGHTS.MEDIUM} resetSpacing>
            {t("job_search_prefiltered.netlinking_block.see_more")}
          </Text>
        </Link>
      )}
    </section>
  )
}
